import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Save, Building2, Shield, Mail } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import LogiRentImportPanel from "../../components/LogiRentImportPanel";
import StripeCredentialsPanel from "../../components/StripeCredentialsPanel";
import MigrationPanel from "../../components/MigrationPanel";

export default function Settings() {
  const [s, setS] = useState(null);
  const [pecList, setPecList] = useState([]);
  const [newEnte, setNewEnte] = useState("");
  const [newPec, setNewPec] = useState("");

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/settings"), api.get("/pec-rubrica")]);
    setS(a.data); setPecList(p.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", s);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error("Errore"); }
  };

  const addPec = async (e) => {
    e.preventDefault();
    if (!newEnte || !newPec) return;
    await api.post(`/pec-rubrica?ente=${encodeURIComponent(newEnte)}&pec=${encodeURIComponent(newPec)}`);
    toast.success("PEC salvata in rubrica");
    setNewEnte(""); setNewPec(""); load();
  };

  const seedPec = async () => {
    const r = await api.post("/seed/pec-rubrica");
    toast.success(`+${r.data.added} PEC importate (totale ${r.data.total})`);
    load();
  };

  if (!s) return <div className="text-white/40">Caricamento…</div>;

  const upd = (k) => (e) => setS({ ...s, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tight">Impostazioni</h1>
        <p className="text-sm text-white/45 mt-1">Dati società, franchigie assicurative, rubrica PEC enti verbalizzanti</p>
      </div>

      <form onSubmit={save} className="grid lg:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Building2 size={16} className="text-[#ff0033]" />
            <div className="font-display font-bold uppercase tracking-wider text-sm">Società</div>
          </div>
          <div className="space-y-3">
            {[
              ["ragione_sociale", "Ragione Sociale"],
              ["piva", "P.IVA"],
              ["codice_fiscale", "Codice Fiscale"],
              ["sede_legale", "Sede Legale"],
              ["sede_operativa", "Sede Operativa"],
              ["pec", "PEC"],
              ["email", "Email"],
              ["telefono", "Telefono"],
              ["codice_univoco", "Codice Univoco SDI"],
            ].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-white/40">{l}</label>
                <input data-testid={`s-${k}`} value={s[k] || ""} onChange={upd(k)}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#ff0033]/60 focus:outline-none" />
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-5">
            <Shield size={16} className="text-[#ff0033]" />
            <div className="font-display font-bold uppercase tracking-wider text-sm">Franchigie Assicurative</div>
          </div>
          <p className="text-xs text-white/45 mb-4">Modificabili in autonomia, applicate automaticamente in ogni contratto stampato.</p>
          <div className="space-y-3">
            {[
              ["franchigia_rca", "Franchigia RCA"],
              ["franchigia_furto", "Franchigia Furto"],
              ["franchigia_incendio", "Franchigia Incendio"],
              ["franchigia_kasko", "Franchigia Kasko"],
            ].map(([k, l]) => (
              <div key={k} className="flex items-center gap-3">
                <label className="text-xs flex-1">{l}</label>
                <div className="flex items-center gap-1 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm w-44">
                  <span className="text-white/40">€</span>
                  <input data-testid={`s-${k}`} type="number" step="0.01" value={s[k] || 0} onChange={upd(k)}
                    className="bg-transparent flex-1 text-right focus:outline-none" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Lingua predefinita stampa</div>
            <select value={s.lingua_default} onChange={upd("lingua_default")}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="it">Italiano</option><option value="en">English</option>
            </select>
          </div>
        </motion.div>

        <div className="lg:col-span-2 flex justify-end">
          <button data-testid="settings-save" type="submit" className="btn-neon px-7 py-3 rounded-full text-sm inline-flex items-center gap-2">
            <Save size={14} /> Salva Impostazioni
          </button>
        </div>
      </form>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-[#ff0033]" />
            <div className="font-display font-bold uppercase tracking-wider text-sm">Rubrica PEC Enti Verbalizzanti</div>
          </div>
          <button onClick={seedPec} className="text-xs px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/10">
            Importa PEC starter
          </button>
        </div>
        <form onSubmit={addPec} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 mb-5">
          <input data-testid="pec-ente" placeholder="Ente (es. Polizia Locale Roma)" value={newEnte} onChange={(e) => setNewEnte(e.target.value)}
            className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="pec-address" placeholder="indirizzo@pec.it" value={newPec} onChange={(e) => setNewPec(e.target.value)}
            className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="pec-add" className="btn-neon px-4 rounded-lg text-xs">Salva PEC</button>
        </form>
        <div className="max-h-72 overflow-auto divide-y divide-white/5">
          {pecList.map((p) => (
            <div key={p.pec} className="py-2 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{p.ente}</div>
                <div className="text-[11px] text-white/40 font-mono">{p.pec}</div>
              </div>
            </div>
          ))}
          {pecList.length === 0 && <div className="text-center py-6 text-white/40 text-sm">Nessuna PEC in rubrica. Importa quelle starter o aggiungile manualmente.</div>}
        </div>
      </div>

      <StripeCredentialsPanel />

      <LogiRentImportPanel />

      <MigrationPanel />
    </div>
  );
}
